import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useOutletContext } from 'react-router-dom';
import { getBoard, postBoardAction } from '../../../api/twoDays/board.api';
import { pilotWorkboard } from './mockData';
import { adjustedDueAt, effectiveDue } from './derive';
import { workboardReducer as baseReducer } from './actions';
import { withPeerScenario } from './mockCompleteProject';

const workboardReducer = (state, action) => {
  if (action.type === '@load') return action.data;
  if (action.type === '@peerScenario') {
    return withPeerScenario(pilotWorkboard, action.key, state?.viewerId);
  }
  return baseReducer(state, action);
};

/**
 * 워크보드 데이터.
 *
 * 2days 백엔드가 아직 없어 목 데이터를 돌려준다.
 * API 가 생기면 fetchWorkboard 안만 실제 호출(예: GET /twodays/projects/:id/workboard)로 바꾸면 된다
 * — 화면은 mockData.js 상단에 적은 Workboard 모양만 본다.
 */
const fetchWorkboard = async (projectId) => {
  if (projectId !== pilotWorkboard.project.id) {
    throw new Error('Project not found');
  }
  return pilotWorkboard;
};

/**
 * 워크보드 데이터 + 행동.
 * 행동(dispatch)은 지금 화면 상태만 바꾼다 — 새로고침하면 목 데이터로 돌아간다.
 *
 * @param viewerOverride 목 데이터 전용. ?as=mp 처럼 다른 팀원 시점으로 본다 (기획자 화면 확인용)
 * @param peerKey 목 데이터 전용. Complete project 상태 (?peer=)
 */
export const useWorkboard = (projectId, viewerOverride, peerKey) => {
  const [status, setStatus] = useState({ isLoading: true, isError: false });
  const [data, dispatch] = useReducer(workboardReducer, null);

  useEffect(() => {
    if (!projectId) return undefined; // 실제 보드(/2days/board/:slug)를 볼 때는 쓰지 않는다
    let cancelled = false;
    setStatus({ isLoading: true, isError: false });
    fetchWorkboard(projectId)
      .then((d) => {
        if (cancelled) return;
        const viewerId =
          viewerOverride && d.members.some((m) => m.id === viewerOverride)
            ? viewerOverride
            : d.viewerId;
        dispatch({ type: '@load', data: withPeerScenario(d, peerKey ?? 'locked', viewerId) });
        setStatus({ isLoading: false, isError: false });
      })
      .catch(() => !cancelled && setStatus({ isLoading: false, isError: true }));
    return () => {
      cancelled = true;
    };
  }, [projectId, viewerOverride, peerKey]);

  return { data, dispatch, ...status };
};

/** 실제 보드 다시 불러오는 간격 — 다른 팀원의 행동을 반영한다 */
// 알림이 실시간에 가깝게 오도록 짧게 (새로고침 없이 목록·배지가 바뀐다)
const POLL_MS = 15_000;

/** 서버 오류 → 화면에서 쓰는 이름 */
const errorKind = (e) => {
  const status = e?.response?.status;
  if (status === 401) return 'login';
  if (status === 403) return 'notJoined';
  if (status === 404) return 'notFound';
  return 'error';
};

/**
 * 실제 팀 워크보드 (/2days/board/:slug).
 * 행동은 화면을 먼저 바꾸고(낙관적) 서버에 보낸 뒤, 서버가 돌려준 보드 전체로 맞춘다.
 * 서버가 거절하면 다시 불러와 되돌리고 actionError 를 채운다. 30초마다 새로 불러온다.
 */
export const useLiveWorkboard = (slug) => {
  const [status, setStatus] = useState({ isLoading: true, isError: false, errorKind: null });
  const [actionError, setActionError] = useState(null);
  const [data, localDispatch] = useReducer(workboardReducer, null);
  const pending = useRef(0);
  // 보는 중에 막히면(예: 운영진이 이탈 처리) 입장 화면으로 바꾸고 더 불러오지 않는다
  const blocked = useRef(false);

  const load = useCallback(
    (initial = false) =>
      blocked.current
        ? Promise.resolve()
        : getBoard(slug)
            .then((d) => {
              // 보내는 중인 행동이 있으면 덮어쓰지 않는다 — 응답이 오면 그걸로 맞춘다
              if (pending.current > 0 && !initial) return;
              localDispatch({ type: '@load', data: d });
              setStatus({ isLoading: false, isError: false, errorKind: null });
            })
            .catch((e) => {
              const forbidden = e?.response?.status === 403;
              if (forbidden) blocked.current = true;
              if (initial || forbidden) {
                setStatus({ isLoading: false, isError: true, errorKind: errorKind(e) });
              }
            }),
    [slug],
  );

  useEffect(() => {
    if (!slug) return undefined; // 목 보드(/2days/projects/:projectId)를 볼 때는 쓰지 않는다
    setStatus({ isLoading: true, isError: false, errorKind: null });
    load(true);
    const id = window.setInterval(() => load(false), POLL_MS);
    const onFocus = () => load(false);
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const dispatch = useCallback(
    (action) => {
      if (action.type === 'setViewer') return; // 개발용 시점 토글은 실제 보드에서 쓰지 않는다
      localDispatch(action);
      pending.current += 1;
      postBoardAction(slug, action)
        .then((d) => localDispatch({ type: '@load', data: d }))
        .catch((e) => {
          setActionError(
            e?.response?.data?.msg || 'Couldn’t save that. Check your connection and try again.',
          );
          // 거절됐으면 서버 상태로 되돌린다
          return load(true);
        })
        .finally(() => {
          pending.current = Math.max(0, pending.current - 1);
        });
    },
    [slug, load],
  );

  return {
    data,
    dispatch,
    ...status,
    actionError,
    clearActionError: () => setActionError(null),
    refresh: () => load(false),
  };
};

/**
 * 화면 기준 시각. 1분마다 갱신한다 (offline 표시·보정 기한·Now 선이 움직인다).
 * 목 데이터는 시안과 같은 장면을 보여주려고 시각을 고정한다.
 *
 * 고정 시각은 state 로 옮기지 않고 바로 돌려준다 — 데이터가 막 도착한 렌더에서
 * 화면들이 실제 시각(프로젝트 기간 밖)을 한 번 보고 지나가지 않게 하기 위해서다.
 */
export const useNow = (frozen) => {
  const [live, setLive] = useState(() => new Date());
  useEffect(() => {
    if (frozen) return undefined;
    const id = window.setInterval(() => setLive(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, [frozen]);
  return useMemo(() => (frozen ? new Date(frozen) : live), [frozen, live]);
};

/**
 * 워크보드 레이아웃이 한 번 불러온 데이터를 하위 화면에 나눠준다 (Outlet context).
 * { data, dispatch, now, displayTz, setDisplayTz, memberById, taskById, viewer }
 */
export const WorkboardCtxOverride = createContext(null);
export const useWorkboardCtx = () => {
  const outlet = useOutletContext();
  // 워크보드 밖(Ops Lead inbox)에서 댓글 컴포넌트를 쓸 때는 직접 넘긴 값을 쓴다
  return useContext(WorkboardCtxOverride) ?? outlet;
};

/** 보정 기한 관련 값을 한 번에 — 카드·리스트·타임라인이 같이 쓴다 */
export const dueInfo = (ctx, task) => ({
  adjusted: adjustedDueAt(task, ctx.taskById, ctx.now, ctx.data.project.endAt),
  effective: effectiveDue(task, ctx.taskById, ctx.now, ctx.data.project.endAt),
  original: new Date(task.dueAt),
});
